/**
 * @fileoverview Defines the application's comprehensive color palette, derived from the existing theme.
 * This file includes base colors and light/dark shades for each, organized into a semantic
 * structure for flexible and consistent use in components.
 */

const colors = {
  // --- BASE PALETTE (derived from the original CSS theme) ---
  brand: {
    primary: '#2e37a4',      // Main theme blue.
    primaryLight: '#4f5bcf', // Lighter shade for hover states.
    primaryDark: '#232b82',  // Darker shade for active states.
    secondary: '#00d3c7',    // Teal color for highlights.
    secondaryLight: '#66e0da',
    secondaryDark: '#00a89f',
  },
  ui: {
    background: 'rgba(51, 53, 72, 0.05)', // Main light background from body style.
    surface: '#ffffff',        // White for cards, modals, etc.
    border: '#dee2e6',         // Standard border color for tables and elements.
    focusBorder: 'rgba(46, 55, 164, 0.1)', // Border color for focused inputs.
    textMain: '#333448',       // Main text color.
    textSubtle: '#565656',     // Lighter text for secondary information.
    textOnBrand: '#ffffff',    // White text used on primary color backgrounds.
  },
  status: {
    success: '#55ce63',        // Green for success states.
    successLight: '#a1e9a9',
    successDark: '#36a246',
    warning: '#ffbc34',        // Yellow for warnings.
    warningLight: '#ffd57a',
    warningDark: '#d99e2b',
    danger: '#f62d51',         // Red for danger and errors.
    dangerLight: '#f96c83',
    dangerDark: '#c42441',
    info: '#009efb',           // Blue for informational messages.
    infoLight: '#66c2fc',
    infoDark: '#007fca',
  },

  // --- ROLE-SPECIFIC PALETTES (using colors from the base theme) ---
  role: {
    admin: {
      primary: '#2e37a4',      // Admin role uses the primary brand color.
      primaryLight: '#4f5bcf',
      primaryDark: '#232b82',
      background: 'rgba(46, 55, 164, 0.05)',
    },
    doctor: {
      primary: '#009efb',      // Doctor role uses the theme's info blue.
      primaryLight: '#66c2fc',
      primaryDark: '#007fca',
      background: 'rgba(0, 158, 251, 0.05)',
    },
  },

  // --- CATEGORY-SPECIFIC PALETTES (using colors from the base theme) ---
  category: {
    scheduling: {
      primary: '#7460ee',      // A distinct purple from the theme.
      primaryLight: '#a395f4',
      primaryDark: '#5543d3',
      background: 'rgba(116, 96, 238, 0.05)',
    },
    clinical: {
      primary: '#55ce63',      // Maps to the theme's success/green color.
      primaryLight: '#a1e9a9',
      primaryDark: '#36a246',
      background: 'rgba(85, 206, 99, 0.05)',
    },
    users: {
      primary: '#ffbc34',      // Maps to the theme's warning/yellow color.
      primaryLight: '#ffd57a',
      primaryDark: '#d99e2b',
      background: 'rgba(255, 188, 52, 0.05)',
    },
    financial: {
      primary: '#00d3c7',      // Maps to the theme's secondary/teal color.
      primaryLight: '#66e0da',
      primaryDark: '#00a89f',
      background: 'rgba(0, 211, 199, 0.05)',
    },
    config: {
      primary: '#565656',      // Uses a neutral, subtle text color.
      primaryLight: '#8c8c8c',
      primaryDark: '#3a3a3a',
      background: 'rgba(86, 86, 86, 0.05)',
    },
  },

  // --- PAYMENT & FINANCIAL PROGRESS PALETTE ---
  payment: {
    // Percentage based statuses
    ranges: {
      critical: { // 0% - 25% paid
        color: '#f62d51',
        bg: '#fff1f0',
        border: '#ffa39e',
        label: 'Critical'
      },
      low: { // 26% - 50% paid
        color: '#fa8c16',
        bg: '#fff7e6',
        border: '#ffd591',
        label: 'Low'
      },
      partial: { // 51% - 75% paid
        color: '#1890ff',
        bg: '#e6f7ff',
        border: '#91d5ff',
        label: 'Partial'
      },
      substantial: { // 76% - 99% paid
        color: '#13c2c2',
        bg: '#e6fffb',
        border: '#87e8de',
        label: 'Substantial'
      },
      completed: { // 100% paid
        color: '#52c41a',
        bg: '#f6ffed',
        border: '#b7eb8f',
        label: 'Paid Full'
      },
    },
    // Special payment states
    states: {
      split: { // Mult-part payments
        color: '#722ed1',
        bg: '#f9f0ff',
        border: '#d3adf7',
        label: 'Split Payment'
      },
      pending: { // Not yet started
        color: '#8c8c8c',
        bg: '#f5f5f5',
        border: '#d9d9d9',
        label: 'Awaiting'
      },
      overdue: { // Past due date
        color: '#ff4d4f',
        bg: '#fff2f0',
        border: '#ffccc7',
        label: 'Overdue'
      },
      refunded: { // Money returned
        color: '#eb2f96',
        bg: '#fff0f6',
        border: '#ffadd2',
        label: 'Refunded'
      }
    }
  }
};

export default colors;
